import {
  formatCodeableConcept,
  formatObservationValue as medplumFormatObservationValue,
  type WithId,
} from '@medplum/core';
import type { Observation } from '@medplum/fhirtypes';
import { getObservationIdentifier } from '../../utils/observation';
import type { ValueSetCode } from './ValueSetCombobox';

export const getSocialHistoryTitle = (observation: Observation): string => {
  return formatCodeableConcept(observation.code) || observation.code?.coding?.[0]?.code || '';
};

export const formatSocialHistoryValue = (observation: Observation): string => {
  return medplumFormatObservationValue(observation) || '—';
};

/** Keep one row per social-history topic (latest by effectiveDateTime), falling back to FHIR id for uncoded observations so they don't disappear. */
export const groupLatestByIdentifier = (observations: WithId<Observation>[]): WithId<Observation>[] => {
  const byIdentifier = new Map<string, WithId<Observation>>();
  for (const obs of observations) {
    const key = getObservationIdentifier(obs) || obs.id;
    if (!key) continue;
    const existing = byIdentifier.get(key);
    if (!existing) {
      byIdentifier.set(key, obs);
      continue;
    }
    const existingTime = new Date(existing.effectiveDateTime ?? '').getTime();
    const candidateTime = new Date(obs.effectiveDateTime ?? '').getTime();
    if (candidateTime > existingTime) byIdentifier.set(key, obs);
  }
  return Array.from(byIdentifier.values()).sort(
    (a, b) => new Date(b.effectiveDateTime ?? '').getTime() - new Date(a.effectiveDateTime ?? '').getTime(),
  );
};

const LOINC_SYSTEM = 'http://loinc.org';

export const SOCIAL_HISTORY_TOPICS: readonly ValueSetCode[] = [
  { system: LOINC_SYSTEM, code: '72166-2', display: 'Tobacco smoking status' },
  { system: LOINC_SYSTEM, code: '11331-6', display: 'History of alcohol use' },
  { system: LOINC_SYSTEM, code: '11341-5', display: 'History of Occupation' },
  { system: LOINC_SYSTEM, code: '76690-7', display: 'Sexual orientation' },
];
